import './atlas.css';

const TILE_SIZE = 256;
const MIN_ZOOM = 3;
const MAX_ZOOM = 10;
const DEFAULT_PLAYBACK_MS = 3000;
const FLY_MS = 950;
const THEME_STORAGE_KEY = 'awesomebible.theme';
const THEME_CHOICES = ['light', 'dark', 'system'];

const BASE_LAYERS = {
    standard: {
        label: 'Street map',
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '© OpenStreetMap contributors',
    },
    topographic: {
        label: 'Topographical',
        url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: '© OpenTopoMap contributors, SRTM · © OpenStreetMap contributors',
    },
    humanitarian: {
        label: 'Humanitarian',
        url: 'https://tile-{s}.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        subdomains: ['a', 'b', 'c'],
        attribution: '© OpenStreetMap contributors, HOT style',
    },
};

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function toRad(degrees) {
    return degrees * Math.PI / 180;
}

function easeInOut(value) {
    return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
}

function project(lat, lng, zoom) {
    const scale = TILE_SIZE * Math.pow(2, zoom);
    const sinLat = Math.sin(toRad(clamp(lat, -85.05112878, 85.05112878)));
    return {
        x: (lng + 180) / 360 * scale,
        y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
    };
}

function unproject(x, y, zoom) {
    const scale = TILE_SIZE * Math.pow(2, zoom);
    const lng = x / scale * 360 - 180;
    const n = Math.PI - 2 * Math.PI * y / scale;
    const lat = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
    return { lat, lng };
}

function refreshManna(element) {
    if (window.manna && typeof window.manna.tag === 'function') {
        window.manna.tag(element);
    }
}

function escapeHtml(value) {
    return String(value).replace(/[&<>'"]/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;',
    }[char]));
}

function confidenceClass(confidence) {
    return `atlas-confidence-${String(confidence || 'medium').toLowerCase()}`;
}

function safeMatchMedia(query) {
    if (!window.matchMedia) {
        return null;
    }
    try {
        return window.matchMedia(query);
    } catch (error) {
        return null;
    }
}

function storedThemePreference() {
    if (window.AwesomeBibleTheme && typeof window.AwesomeBibleTheme.storedPreference === 'function') {
        return window.AwesomeBibleTheme.storedPreference();
    }
    let preference = document.documentElement.getAttribute('data-awesomebible-theme-preference') || 'system';
    try {
        preference = window.localStorage.getItem(THEME_STORAGE_KEY) || preference;
    } catch (error) {
        // Keep the server/inline-script preference when localStorage is unavailable.
    }
    return THEME_CHOICES.includes(preference) ? preference : 'system';
}

function applyThemePreference(preference, options = {}) {
    if (window.AwesomeBibleTheme && typeof window.AwesomeBibleTheme.applyPreference === 'function') {
        return window.AwesomeBibleTheme.applyPreference(preference, options);
    }
    const safePreference = THEME_CHOICES.includes(preference) ? preference : 'system';
    const mediaQuery = safeMatchMedia('(prefers-color-scheme: dark)');
    const systemTheme = mediaQuery && mediaQuery.matches ? 'dark' : 'light';
    const resolvedTheme = safePreference === 'system' ? systemTheme : safePreference;
    document.documentElement.setAttribute('data-awesomebible-theme-preference', safePreference);
    document.documentElement.setAttribute('data-awesomebible-theme', resolvedTheme);
    if (!options.temporary) {
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, safePreference);
        } catch (error) {
            // Theme persistence is progressive enhancement.
        }
    }
    return resolvedTheme;
}

function readJourneyData() {
    const script = document.getElementById('atlas-journey-data');
    if (!script) {
        return [];
    }
    try {
        return JSON.parse(script.textContent);
    } catch (error) {
        console.error('Could not parse atlas journey data', error);
        return [];
    }
}

class BibleAtlas {
    constructor(root, journeys) {
        this.root = root;
        this.journeys = journeys;
        this.journey = journeys[0];
        this.eventIndex = 0;
        this.zoom = this.journey.center.zoom;
        this.center = {
            lat: this.journey.center.lat,
            lng: this.journey.center.lng,
        };
        this.baseLayer = 'topographic';
        this.playbackDelay = DEFAULT_PLAYBACK_MS;
        this.playTimer = null;
        this.flyFrame = null;
        this.cardVisible = true;
        this.cardDismissed = false;
        this.cardCollapsed = false;
        this.cardOffset = { x: 0, y: 0 };
        this.cardDrag = null;
        this.themeMediaQuery = safeMatchMedia('(prefers-color-scheme: dark)');
        this.themePreferenceBeforeTheater = null;
        this.layers = {
            route: true,
            places: true,
            uncertainty: true,
            cards: true,
        };
        this.drag = null;
        this.elements = this.collectElements();
        this.bind();
        this.renderRouteSwitcher();
        this.selectJourney(this.journey.id, { reset: true });
    }

    collectElements() {
        return {
            map: this.root.querySelector('[data-atlas-map]'),
            tiles: this.root.querySelector('[data-atlas-tiles]'),
            routeLayer: this.root.querySelector('[data-atlas-route-layer]'),
            markers: this.root.querySelector('[data-atlas-markers]'),
            cards: this.root.querySelector('[data-atlas-card-layer]'),
            routeSwitcher: this.root.querySelector('[data-atlas-route-switcher]'),
            timeline: this.root.querySelector('[data-atlas-timeline]'),
            title: this.root.querySelector('[data-atlas-title]'),
            summary: this.root.querySelector('[data-atlas-summary]'),
            routeSummary: this.root.querySelector('[data-atlas-route-summary]'),
            period: this.root.querySelector('[data-atlas-period]'),
            primaryReference: this.root.querySelector('[data-atlas-primary-reference]'),
            confidence: this.root.querySelector('[data-atlas-confidence]'),
            scrubber: this.root.querySelector('[data-atlas-scrubber]'),
            stepReadout: this.root.querySelector('[data-atlas-step-readout]'),
            play: this.root.querySelector('[data-atlas-play]'),
            loop: this.root.querySelector('[data-atlas-loop]'),
            speed: this.root.querySelector('[data-atlas-speed]'),
            speedReadout: this.root.querySelector('[data-atlas-speed-readout]'),
            baseLayer: this.root.querySelector('[data-atlas-base-layer]'),
            attribution: this.root.querySelector('[data-atlas-attribution]'),
            themeChoices: this.root.querySelectorAll('[data-atlas-theme-choice]'),
            theater: this.root.querySelector('[data-atlas-theater]'),
            popoverToggles: this.root.querySelectorAll('[data-atlas-popover-toggle]'),
        };
    }

    bind() {
        this.root.querySelector('[data-atlas-prev]').addEventListener('click', () => this.step(-1));
        this.root.querySelector('[data-atlas-next]').addEventListener('click', () => this.step(1));
        this.root.querySelector('[data-atlas-reset]').addEventListener('click', () => this.resetView());
        this.root.querySelector('[data-atlas-center]').addEventListener('click', () => this.fitJourney({ animate: true }));
        this.root.querySelector('[data-atlas-zoom-in]').addEventListener('click', () => this.setZoom(this.zoom + 1));
        this.root.querySelector('[data-atlas-zoom-out]').addEventListener('click', () => this.setZoom(this.zoom - 1));
        this.elements.play.addEventListener('click', () => this.togglePlayback());
        this.elements.scrubber.addEventListener('input', (event) => this.setEventIndex(parseInt(event.target.value, 10), { animate: true }));
        if (this.elements.speed) {
            this.elements.speed.addEventListener('input', (event) => this.setPlaybackDelay(parseInt(event.target.value, 10)));
            this.setPlaybackDelay(parseInt(this.elements.speed.value, 10), { keepTimer: true });
        }
        if (this.elements.baseLayer) {
            this.elements.baseLayer.addEventListener('change', (event) => {
                this.baseLayer = event.target.value;
                this.renderMap();
            });
        }
        this.root.querySelectorAll('[data-atlas-layer]').forEach((input) => {
            input.addEventListener('change', () => {
                this.layers[input.dataset.atlasLayer] = input.checked;
                this.renderMap();
                this.renderTimeline();
            });
        });
        this.elements.map.addEventListener('wheel', (event) => {
            event.preventDefault();
            this.setZoom(this.zoom + (event.deltaY < 0 ? 1 : -1));
        }, { passive: false });
        this.elements.map.addEventListener('pointerdown', (event) => this.startDrag(event));
        window.addEventListener('pointermove', (event) => {
            this.moveDrag(event);
            this.moveCardDrag(event);
        });
        window.addEventListener('pointerup', () => {
            this.stopDrag();
            this.stopCardDrag();
        });
        if (this.elements.themeChoices.length) {
            this.elements.themeChoices.forEach((input) => {
                input.addEventListener('change', () => this.setThemePreference(input.value));
            });
            this.syncThemeChoices(storedThemePreference());
        }
        document.addEventListener('awesomebible:themechange', () => this.renderMap());
        if (this.themeMediaQuery) {
            const handleSystemThemeChange = () => {
                if (storedThemePreference() === 'system') {
                    applyThemePreference('system');
                    this.renderMap();
                }
            };
            if (typeof this.themeMediaQuery.addEventListener === 'function') {
                this.themeMediaQuery.addEventListener('change', handleSystemThemeChange);
            } else if (typeof this.themeMediaQuery.addListener === 'function') {
                this.themeMediaQuery.addListener(handleSystemThemeChange);
            }
        }
        if (this.elements.theater) {
            this.elements.theater.addEventListener('click', () => this.startTheaterMode());
        }
        this.elements.popoverToggles.forEach((toggle) => {
            toggle.addEventListener('click', (event) => {
                event.stopPropagation();
                this.togglePopover(toggle.dataset.atlasPopoverToggle);
            });
        });
        document.addEventListener('click', (event) => {
            if (!this.root.contains(event.target)) {
                this.closePopovers();
                return;
            }
            if (!event.target.closest('[data-atlas-popover], [data-atlas-popover-toggle]')) {
                this.closePopovers();
            }
        });
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.closePopovers();
            }
        });
        window.addEventListener('fullscreenchange', () => this.handleFullscreenChange());
        window.addEventListener('resize', () => this.renderMap());
    }

    togglePopover(name) {
        const toggle = this.root.querySelector(`[data-atlas-popover-toggle="${name}"]`);
        const popover = this.root.querySelector(`[data-atlas-popover="${name}"]`);
        const shouldOpen = !(toggle && toggle.getAttribute('aria-expanded') === 'true');
        this.closePopovers();
        if (toggle && popover && shouldOpen) {
            toggle.setAttribute('aria-expanded', 'true');
            popover.classList.add('is-open');
        }
    }

    closePopovers() {
        this.elements.popoverToggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
        this.root.querySelectorAll('[data-atlas-popover]').forEach((popover) => popover.classList.remove('is-open'));
    }

    setThemePreference(preference, options = {}) {
        applyThemePreference(preference, options);
        this.syncThemeChoices(preference);
        this.renderMap();
    }

    syncThemeChoices(preference) {
        this.elements.themeChoices.forEach((input) => {
            input.checked = input.value === preference;
        });
    }

    startTheaterMode() {
        this.themePreferenceBeforeTheater = storedThemePreference();
        this.setThemePreference('dark', { temporary: true });
        this.root.classList.add('is-theater-mode');
        if (!this.playTimer) {
            this.startPlayback();
        }
        const fullscreenTarget = this.elements.map;
        if (fullscreenTarget.requestFullscreen) {
            fullscreenTarget.requestFullscreen().catch(() => {
                this.renderMap();
            });
        }
        this.renderMap();
    }

    handleFullscreenChange() {
        const isFullscreen = document.fullscreenElement === this.elements.map;
        this.elements.map.classList.toggle('is-fullscreen', isFullscreen);
        this.root.classList.toggle('is-theater-mode', isFullscreen);
        if (!isFullscreen && this.themePreferenceBeforeTheater) {
            this.setThemePreference(this.themePreferenceBeforeTheater);
            this.themePreferenceBeforeTheater = null;
        }
        window.setTimeout(() => this.renderMap(), 80);
    }

    renderRouteSwitcher() {
        this.elements.routeSwitcher.innerHTML = '';
        this.journeys.forEach((journey) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'atlas-route-button';
            button.dataset.routeId = journey.id;
            button.innerHTML = `<strong>${escapeHtml(journey.short_title)}</strong><span>${escapeHtml(journey.category)}</span>`;
            button.addEventListener('click', () => this.selectJourney(journey.id, { reset: true }));
            this.elements.routeSwitcher.appendChild(button);
        });
    }

    selectJourney(id, options = {}) {
        const selected = this.journeys.find((journey) => journey.id === id);
        if (!selected) {
            return;
        }
        this.stopPlayback();
        this.cancelFly();
        this.journey = selected;
        this.eventIndex = 0;
        this.cardVisible = true;
        this.cardDismissed = false;
        this.cardCollapsed = false;
        this.cardOffset = { x: 0, y: 0 };
        if (options.reset) {
            this.resetView();
        }
        this.elements.title.textContent = selected.title;
        this.elements.summary.textContent = selected.summary;
        this.elements.period.textContent = selected.period;
        this.elements.primaryReference.textContent = selected.primary_reference;
        this.elements.confidence.textContent = selected.confidence;
        this.elements.scrubber.max = Math.max(selected.events.length - 1, 0);
        this.root.querySelectorAll('[data-route-id]').forEach((button) => {
            button.classList.toggle('is-active', button.dataset.routeId === selected.id);
        });
        refreshManna(this.elements.routeSummary);
        this.renderTimeline();
        this.renderMap();
    }

    resetView() {
        this.cancelFly();
        this.center = {
            lat: this.journey.center.lat,
            lng: this.journey.center.lng,
        };
        this.zoom = this.journey.center.zoom;
        this.cardVisible = true;
        this.cardDismissed = false;
        this.cardOffset = { x: 0, y: 0 };
        this.renderMap();
    }

    fitJourney(options = {}) {
        const bounds = this.journey.bounds;
        const targetCenter = {
            lat: (bounds.north + bounds.south) / 2,
            lng: (bounds.east + bounds.west) / 2,
        };
        const lngSpan = Math.max(bounds.east - bounds.west, 1);
        const latSpan = Math.max(bounds.north - bounds.south, 1);
        const mapWidth = Math.max(this.elements.map.clientWidth, 320);
        const mapHeight = Math.max(this.elements.map.clientHeight, 320);
        const lngZoom = Math.floor(Math.log2(mapWidth * 360 / (lngSpan * TILE_SIZE))) - 1;
        const latZoom = Math.floor(Math.log2(mapHeight * 170 / (latSpan * TILE_SIZE))) - 1;
        const targetZoom = clamp(Math.min(lngZoom, latZoom), MIN_ZOOM, MAX_ZOOM);
        if (options.animate) {
            this.animateTo(targetCenter, targetZoom);
        } else {
            this.center = targetCenter;
            this.zoom = targetZoom;
            this.renderMap();
        }
    }

    setZoom(zoom) {
        this.cancelFly();
        this.zoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
        this.renderMap();
    }

    setEventIndex(index, options = {}) {
        const maxIndex = this.journey.events.length - 1;
        const nextIndex = clamp(index, 0, maxIndex);
        const eventData = this.journey.events[nextIndex];
        this.eventIndex = nextIndex;
        if (options.toggleCard || options.showCard) {
            this.cardVisible = true;
            this.cardDismissed = false;
        } else if (!this.cardDismissed) {
            this.cardVisible = true;
        }
        this.cardOffset = { x: 0, y: 0 };
        if (this.cardVisible) {
            this.flashCardTransition();
        }
        this.renderTimeline();
        if (options.animate) {
            this.animateTo({ lat: eventData.lat, lng: eventData.lng }, Math.max(this.zoom, 7));
        } else {
            this.centerOnEvent(eventData, { keepZoom: true });
            this.renderMap();
        }
    }

    step(direction) {
        const maxIndex = this.journey.events.length - 1;
        let nextIndex = this.eventIndex + direction;
        if (nextIndex > maxIndex) {
            nextIndex = this.elements.loop.checked ? 0 : maxIndex;
        }
        if (nextIndex < 0) {
            nextIndex = this.elements.loop.checked ? maxIndex : 0;
        }
        this.setEventIndex(nextIndex, { animate: true });
    }

    togglePlayback() {
        if (this.playTimer) {
            this.stopPlayback();
        } else {
            this.startPlayback();
        }
    }

    startPlayback() {
        this.elements.play.textContent = '⏸';
        this.elements.play.setAttribute('aria-label', 'Pause route');
        this.step(1);
        this.startPlaybackTimer();
    }

    startPlaybackTimer() {
        if (this.playTimer) {
            window.clearInterval(this.playTimer);
        }
        this.playTimer = window.setInterval(() => {
            const atEnd = this.eventIndex >= this.journey.events.length - 1;
            if (atEnd && !this.elements.loop.checked) {
                this.stopPlayback();
                return;
            }
            this.step(1);
        }, this.playbackDelay);
    }

    setPlaybackDelay(delay, options = {}) {
        this.playbackDelay = clamp(delay || DEFAULT_PLAYBACK_MS, 1000, 8000);
        if (this.elements.speed) {
            this.elements.speed.value = this.playbackDelay;
        }
        if (this.elements.speedReadout) {
            this.elements.speedReadout.textContent = `${(this.playbackDelay / 1000).toFixed(1)}s`;
        }
        if (this.playTimer && !options.keepTimer) {
            this.startPlaybackTimer();
        }
    }

    stopPlayback() {
        if (this.playTimer) {
            window.clearInterval(this.playTimer);
            this.playTimer = null;
        }
        this.elements.play.textContent = '▶';
        this.elements.play.setAttribute('aria-label', 'Play route');
    }

    centerOnEvent(eventData, options = {}) {
        this.center = { lat: eventData.lat, lng: eventData.lng };
        if (!options.keepZoom) {
            this.zoom = Math.max(this.zoom, 7);
        }
    }

    animateTo(targetCenter, targetZoom) {
        this.cancelFly();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.center = targetCenter;
            this.zoom = targetZoom;
            this.renderMap();
            return;
        }
        const startCenter = { lat: this.center.lat, lng: this.center.lng };
        const startZoom = this.zoom;
        const startTime = performance.now();
        const run = (now) => {
            const progress = clamp((now - startTime) / FLY_MS, 0, 1);
            const eased = easeInOut(progress);
            this.center = {
                lat: startCenter.lat + (targetCenter.lat - startCenter.lat) * eased,
                lng: startCenter.lng + (targetCenter.lng - startCenter.lng) * eased,
            };
            this.zoom = clamp(Math.round((startZoom + (targetZoom - startZoom) * eased) * 100) / 100, MIN_ZOOM, MAX_ZOOM);
            this.renderMap();
            if (progress < 1) {
                this.flyFrame = window.requestAnimationFrame(run);
            } else {
                this.flyFrame = null;
            }
        };
        this.flyFrame = window.requestAnimationFrame(run);
    }

    cancelFly() {
        if (this.flyFrame) {
            window.cancelAnimationFrame(this.flyFrame);
            this.flyFrame = null;
        }
    }

    flashCardTransition() {
        this.elements.cards.classList.add('is-transitioning');
        window.setTimeout(() => this.elements.cards.classList.remove('is-transitioning'), 220);
    }

    startDrag(event) {
        if (event.target.closest('.atlas-event-card, .atlas-floating-popover, .atlas-floating-icon, .atlas-museum-chip, button, input, select, label')) {
            return;
        }
        this.cancelFly();
        this.drag = {
            x: event.clientX,
            y: event.clientY,
            center: project(this.center.lat, this.center.lng, this.zoom),
        };
        this.elements.map.setPointerCapture(event.pointerId);
        this.elements.map.classList.add('is-dragging');
    }

    moveDrag(event) {
        if (!this.drag) {
            return;
        }
        const nextCenterPoint = {
            x: this.drag.center.x - (event.clientX - this.drag.x),
            y: this.drag.center.y - (event.clientY - this.drag.y),
        };
        this.center = unproject(nextCenterPoint.x, nextCenterPoint.y, this.zoom);
        this.renderMap();
    }

    stopDrag() {
        this.drag = null;
        this.elements.map.classList.remove('is-dragging');
    }

    startCardDrag(event) {
        event.preventDefault();
        event.stopPropagation();
        this.cardDrag = {
            x: event.clientX,
            y: event.clientY,
            offset: { x: this.cardOffset.x, y: this.cardOffset.y },
        };
        this.elements.cards.classList.add('is-moving-card');
    }

    moveCardDrag(event) {
        if (!this.cardDrag) {
            return;
        }
        this.cardOffset = {
            x: this.cardDrag.offset.x + event.clientX - this.cardDrag.x,
            y: this.cardDrag.offset.y + event.clientY - this.cardDrag.y,
        };
        this.renderMap();
    }

    stopCardDrag() {
        this.cardDrag = null;
        this.elements.cards.classList.remove('is-moving-card');
    }

    getViewport() {
        const width = this.elements.map.clientWidth || 800;
        const height = this.elements.map.clientHeight || 520;
        const zoom = Math.round(this.zoom);
        const centerPoint = project(this.center.lat, this.center.lng, zoom);
        return {
            width,
            height,
            zoom,
            centerPoint,
            left: centerPoint.x - width / 2,
            top: centerPoint.y - height / 2,
        };
    }

    pointForEvent(eventData, viewport) {
        const point = project(eventData.lat, eventData.lng, viewport.zoom);
        return {
            x: point.x - viewport.left,
            y: point.y - viewport.top,
        };
    }

    renderMap() {
        if (!this.journey) {
            return;
        }
        const viewport = this.getViewport();
        this.renderTiles(viewport);
        this.renderRoute(viewport);
        this.renderMarkers(viewport);
        if (this.cardVisible) {
            this.renderCards(viewport);
        } else {
            this.elements.cards.innerHTML = '';
        }
        this.root.classList.toggle('atlas-hide-route', !this.layers.route);
        this.root.classList.toggle('atlas-hide-places', !this.layers.places);
        this.root.classList.toggle('atlas-hide-uncertainty', !this.layers.uncertainty);
        this.root.classList.toggle('atlas-hide-cards', !this.layers.cards);
    }

    tileUrl(layer, z, x, y) {
        const maxTile = Math.pow(2, z);
        const wrappedX = ((x % maxTile) + maxTile) % maxTile;
        let url = layer.url.replace('{z}', z).replace('{x}', wrappedX).replace('{y}', y);
        if (layer.subdomains) {
            url = url.replace('{s}', layer.subdomains[Math.abs(wrappedX + y) % layer.subdomains.length]);
        }
        return url;
    }

    renderTiles(viewport) {
        const z = viewport.zoom;
        const layer = BASE_LAYERS[this.baseLayer] || BASE_LAYERS.standard;
        const minX = Math.floor(viewport.left / TILE_SIZE);
        const maxX = Math.floor((viewport.left + viewport.width) / TILE_SIZE);
        const minY = Math.floor(viewport.top / TILE_SIZE);
        const maxY = Math.floor((viewport.top + viewport.height) / TILE_SIZE);
        const maxTile = Math.pow(2, z);
        const fragments = [];
        for (let x = minX; x <= maxX; x += 1) {
            for (let y = minY; y <= maxY; y += 1) {
                if (y < 0 || y >= maxTile) {
                    continue;
                }
                const src = this.tileUrl(layer, z, x, y);
                const left = x * TILE_SIZE - viewport.left;
                const top = y * TILE_SIZE - viewport.top;
                fragments.push(`<img class="atlas-tile" src="${src}" alt="" style="left:${left}px;top:${top}px" draggable="false" />`);
            }
        }
        this.elements.tiles.innerHTML = fragments.join('');
        if (this.elements.attribution) {
            this.elements.attribution.textContent = layer.attribution;
        }
    }

    renderRoute(viewport) {
        const svg = this.elements.routeLayer;
        svg.setAttribute('viewBox', `0 0 ${viewport.width} ${viewport.height}`);
        svg.setAttribute('width', viewport.width);
        svg.setAttribute('height', viewport.height);
        const points = this.journey.events.map((eventData) => this.pointForEvent(eventData, viewport));
        const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
        svg.innerHTML = `
            <path class="atlas-route-path atlas-route-shadow" d="${path}" />
            <path class="atlas-route-path" d="${path}" style="stroke:${this.journey.color}" />
        `;
    }

    renderMarkers(viewport) {
        this.elements.markers.innerHTML = '';
        this.journey.events.forEach((eventData, index) => {
            const point = this.pointForEvent(eventData, viewport);
            const marker = document.createElement('button');
            marker.type = 'button';
            marker.className = `atlas-marker ${confidenceClass(eventData.confidence)}`;
            marker.classList.toggle('is-active', index === this.eventIndex);
            marker.style.left = `${point.x}px`;
            marker.style.top = `${point.y}px`;
            marker.style.setProperty('--route-color', this.journey.color);
            marker.innerHTML = `<span class="atlas-marker-halo"></span><span class="atlas-marker-dot">${escapeHtml(eventData.order)}</span>`;
            marker.title = `${eventData.title} — ${eventData.reference}`;
            marker.addEventListener('click', () => this.setEventIndex(index, { animate: true, toggleCard: true }));
            this.elements.markers.appendChild(marker);
        });
    }

    renderCards(viewport) {
        const eventData = this.journey.events[this.eventIndex];
        const point = this.pointForEvent(eventData, viewport);
        const isRight = point.x < viewport.width * 0.58;
        const isLow = point.y > viewport.height * 0.55;
        const collapsedClass = this.cardCollapsed ? 'is-collapsed' : '';
        const left = point.x + this.cardOffset.x;
        const top = point.y + this.cardOffset.y;
        this.elements.cards.innerHTML = `
            <article class="atlas-event-card ${isRight ? 'is-right' : 'is-left'} ${isLow ? 'is-high' : ''} ${collapsedClass}" style="left:${left}px;top:${top}px;border-color:${this.journey.color}">
                <div class="atlas-event-card-bar">
                    <button type="button" class="atlas-card-drag" data-atlas-card-drag aria-label="Drag event card">Move</button>
                    <button type="button" class="atlas-card-dismiss" data-atlas-card-dismiss aria-label="Dismiss event card">Hide</button>
                    <button type="button" class="atlas-card-toggle" data-atlas-card-toggle aria-label="Collapse event card">${this.cardCollapsed ? 'Expand' : 'Collapse'}</button>
                </div>
                <p class="atlas-event-eyebrow">Stop ${escapeHtml(eventData.order)} · ${escapeHtml(eventData.place)}</p>
                <div class="atlas-card-body">
                    <h3>${escapeHtml(eventData.title)}</h3>
                    <p>${escapeHtml(eventData.description)}</p>
                    <div class="atlas-event-footer">
                        <span class="atlas-reference-text">${escapeHtml(eventData.reference)}</span>
                        <span class="${confidenceClass(eventData.confidence)}">${escapeHtml(eventData.confidence)} confidence</span>
                    </div>
                </div>
            </article>
        `;
        this.bindCardControls();
        refreshManna(this.elements.cards);
    }

    bindCardControls() {
        const toggle = this.elements.cards.querySelector('[data-atlas-card-toggle]');
        const dismiss = this.elements.cards.querySelector('[data-atlas-card-dismiss]');
        const dragHandle = this.elements.cards.querySelector('[data-atlas-card-drag]');
        if (dismiss) {
            dismiss.addEventListener('click', (event) => {
                event.stopPropagation();
                this.cardVisible = false;
                this.cardDismissed = true;
                this.renderTimeline();
                this.renderMap();
            });
        }
        if (toggle) {
            toggle.addEventListener('click', (event) => {
                event.stopPropagation();
                this.cardCollapsed = !this.cardCollapsed;
                this.renderMap();
            });
        }
        if (dragHandle) {
            dragHandle.addEventListener('pointerdown', (event) => this.startCardDrag(event));
        }
    }

    renderTimeline() {
        this.elements.scrubber.value = this.eventIndex;
        const activeEvent = this.journey.events[this.eventIndex];
        this.elements.stepReadout.textContent = `${this.eventIndex + 1} of ${this.journey.events.length}: ${activeEvent.title}`;
        this.elements.timeline.innerHTML = '';
        this.journey.events.forEach((eventData, index) => {
            const item = document.createElement('li');
            const isSelected = index === this.eventIndex;
            item.className = 'atlas-timeline-item';
            item.classList.toggle('is-soft-active', isSelected && !this.cardVisible);
            item.classList.toggle('is-active', isSelected && this.cardVisible);
            item.innerHTML = `
                <button type="button" style="--route-color:${this.journey.color}">
                    <span>${escapeHtml(eventData.order)}</span>
                    <strong>${escapeHtml(eventData.title)}</strong>
                    <small>${escapeHtml(eventData.reference)} · ${escapeHtml(eventData.place)}</small>
                </button>
            `;
            item.querySelector('button').addEventListener('click', () => this.setEventIndex(index, { animate: true, toggleCard: true }));
            this.elements.timeline.appendChild(item);
        });
    }
}

function initAtlas() {
    const root = document.querySelector('[data-atlas-app]');
    if (!root) {
        return;
    }
    const journeys = readJourneyData();
    if (!journeys.length) {
        return;
    }
    new BibleAtlas(root, journeys);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAtlas);
} else {
    initAtlas();
}
